import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, Search, Truck, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { cn, getStatusColor, getStatusStyle, formatDate } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Where Is Equipment Today — Equipment Overview page (Aug 2026).
// Read-only fleet status board. Status precedence, confirmed by owner:
// Checked Out > Assigned > In Shop > Retired/Sold > Available.
const STATUS_META = {
  checked_out: { label: 'Checked Out', dot: 'bg-amber-500', badge: 'bg-amber-100 text-amber-800 border-amber-200' },
  assigned: { label: 'Assigned', dot: 'bg-blue-500', badge: 'bg-blue-100 text-blue-800 border-blue-200' },
  in_shop: { label: 'In Shop', dot: 'bg-orange-500', badge: 'bg-orange-100 text-orange-800 border-orange-200' },
  retired_sold: { label: 'Retired/Sold', dot: 'bg-slate-400', badge: 'bg-slate-100 text-slate-600 border-slate-200' },
  available: { label: 'Available', dot: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
};
const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'available', label: 'Available' },
  { key: 'checked_out', label: 'Checked Out' },
  { key: 'assigned', label: 'Assigned' },
  { key: 'in_shop', label: 'In Shop' },
  { key: 'retired_sold', label: 'Retired/Sold' },
];

function formatCheckoutTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}

export default function EquipmentOverview() {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/assets/overview`)
      .then(res => {
        if (!mounted) return;
        setSummary(res.data.summary);
        setItems(res.data.items || []);
      })
      .catch(() => toast.error('Could not load Equipment Overview'))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(it => {
      if (statusFilter !== 'all' && it.status_bucket !== statusFilter) return false;
      if (!q) return true;
      return (it.name || '').toLowerCase().includes(q) || (it.asset_number || '').toLowerCase().includes(q);
    });
  }, [items, statusFilter, search]);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6" data-testid="equipment-overview-page">
      <div className="flex items-center gap-2">
        <MapPin className="w-6 h-6 text-slate-700" />
        <h1 className="text-2xl font-bold text-slate-900">Equipment Overview</h1>
      </div>
      <p className="text-sm text-slate-500 -mt-4">Where is everything today, at a glance. Read-only — for editing equipment, use the Equipment tab under Maintenance.</p>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400" data-testid="equipment-overview-loading">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : (
        <>
          {/* Fleet summary */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="equipment-overview-summary">
            {Object.entries(STATUS_META).map(([key, meta]) => (
              <Card
                key={key}
                className={cn('cursor-pointer border transition hover:shadow-md', statusFilter === key ? 'ring-2 ring-slate-400' : '')}
                onClick={() => setStatusFilter(statusFilter === key ? 'all' : key)}
                data-testid={`equipment-overview-summary-${key}`}
              >
                <CardContent className="p-3 flex flex-col gap-1">
                  <div className="flex items-center gap-1.5">
                    <span className={cn('w-2 h-2 rounded-full', meta.dot)} />
                    <span className="text-xs font-medium text-slate-500">{meta.label}</span>
                  </div>
                  <span className="text-2xl font-bold text-slate-900">{summary?.[key] ?? 0}</span>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Filters + search */}
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <div className="flex flex-wrap gap-2" data-testid="equipment-overview-filters">
              {FILTERS.map(f => (
                <Button
                  key={f.key}
                  size="sm"
                  variant={statusFilter === f.key ? 'default' : 'outline'}
                  onClick={() => setStatusFilter(f.key)}
                  data-testid={`equipment-overview-filter-${f.key}`}
                >
                  {f.label}
                </Button>
              ))}
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search name or ID..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-8"
                data-testid="equipment-overview-search"
              />
            </div>
          </div>

          {/* Equipment list */}
          <div className="space-y-2" data-testid="equipment-overview-list">
            {filtered.length === 0 ? (
              <div className="text-center py-16 text-slate-400" data-testid="equipment-overview-empty">
                <Truck className="w-8 h-8 mx-auto mb-2 opacity-40" />
                No equipment matches your filters.
              </div>
            ) : (
              filtered.map(item => {
                const meta = STATUS_META[item.status_bucket] || STATUS_META.available;
                const linkTo = item.status_bucket === 'checked_out'
                  ? (item.job_id ? `/jobs/${item.job_id}` : `/asset/${item.id}`)
                  : item.status_bucket === 'assigned'
                    ? `/jobs/${item.job_id}`
                    : `/asset/${item.id}`;
                return (
                  <Link
                    to={linkTo}
                    key={item.id}
                    className="block"
                    data-testid={`equipment-overview-row-${item.asset_number || item.id}`}
                  >
                    <Card className="hover:border-slate-300 hover:shadow-sm transition">
                      <CardContent className="p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className={cn('w-2.5 h-2.5 rounded-full flex-shrink-0', meta.dot)} />
                          <div className="min-w-0">
                            <div className="font-medium text-slate-900 truncate">{item.name}</div>
                            <div className="text-xs text-slate-500">{item.asset_number} {item.category ? `· ${item.category}` : ''}</div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3 flex-shrink-0 pl-5 sm:pl-0">
                          {item.status_bucket === 'checked_out' && (
                            <div className="text-left sm:text-right text-xs text-slate-500">
                              {item.employee && <div>{item.employee}</div>}
                              {item.job_number && <div>{item.job_number}</div>}
                              {item.checked_out_at && <div>{formatCheckoutTime(item.checked_out_at)}</div>}
                            </div>
                          )}
                          {/* Fix (2026-08-31): the raw job number alone wasn't
                              enough context to know what an assignment is
                              without leaving the page — surface the job's
                              name/type and address (same data already shown
                              on the job's own card elsewhere), plus date and
                              status as a nice-to-have. Deliberately a
                              2-3-line block (not squeezed to one line) so
                              long job names/addresses don't get cut off. */}
                          {item.status_bucket === 'assigned' && item.job_number && (
                            <div className="text-left sm:text-right text-xs text-slate-500 min-w-0 max-w-[220px] sm:max-w-[320px]">
                              {item.job_title && (
                                <div className="font-medium text-slate-700 truncate" title={item.job_title} data-testid={`equipment-overview-job-title-${item.asset_number || item.id}`}>
                                  {item.job_title}
                                </div>
                              )}
                              {item.service_address && (
                                <div className="truncate text-slate-400" title={item.service_address} data-testid={`equipment-overview-job-address-${item.asset_number || item.id}`}>
                                  {item.service_address}
                                </div>
                              )}
                              <div className="flex items-center gap-1.5 sm:justify-end">
                                {item.job_date && <span>{formatDate(item.job_date)}</span>}
                                <span>{item.job_number}</span>
                              </div>
                            </div>
                          )}
                          {item.status_bucket === 'in_shop' && item.in_shop_note && (
                            <div className="text-left sm:text-right text-xs text-slate-500 italic max-w-[220px] truncate" data-testid={`equipment-overview-in-shop-note-${item.asset_number || item.id}`}>
                              "{item.in_shop_note}"
                            </div>
                          )}
                          {item.status_bucket === 'assigned' && item.job_status && (
                            <Badge
                              className={cn('border text-xs', getStatusColor(item.job_status))}
                              style={getStatusStyle(item.job_status) || undefined}
                              data-testid={`equipment-overview-job-status-${item.asset_number || item.id}`}
                            >
                              {item.job_status}
                            </Badge>
                          )}
                          <Badge className={cn('border', meta.badge)} data-testid={`equipment-overview-badge-${item.asset_number || item.id}`}>
                            {meta.label}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}
